import React from 'react';
import { formatDateTime } from 'utils/date.util';

import {
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Divider,
  Flex,
  Button,
  Center,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Image,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';

import { FaExternalLinkAlt, FaCheck, FaTimes, FaClock } from 'react-icons/fa';

import { useColors } from './colors';

const OptimizerTab = ({ listing }) => {
  const {
    bgColor,
    borderColor,
    headingColor,
    subTextColor: labelColor,
    inputColor,
    hoverBg: readOnlyBg,
    successColor,
    errorColor,
    textColor,
    warningColor,
  } = useColors();

  const optimization = listing?.optimization;

  // Helper function to format price
  const formatPrice = (price) => {
    return `$${parseFloat(price || 0).toFixed(2)}`;
  };

  // Get status badge configuration
  const getStatusBadge = (status) => {
    const statusConfig = {
      'not-optimized': { label: 'Not Optimized', colorScheme: 'orange', icon: FaTimes },
      halted: { label: 'Optimizer Halted', colorScheme: 'purple', icon: FaTimes },
      active: { label: 'Active', colorScheme: 'blue', icon: FaClock },
      optimized: { label: 'Optimized', colorScheme: 'green', icon: FaCheck },
    };

    const config = statusConfig[status] || {
      label: status,
      colorScheme: 'gray',
      icon: FaTimes,
    };
    const IconComponent = config.icon;

    return (
      <Badge
        colorScheme={config.colorScheme}
        variant="solid"
        px={3}
        py={1}
        borderRadius="md"
      >
        <HStack spacing={1}>
          <IconComponent size="12px" />
          <Text fontSize="sm" fontWeight="semibold">
            {config.label}
          </Text>
        </HStack>
      </Badge>
    );
  };

  // If no optimization data exists
  if (!optimization) {
    return (
      <VStack spacing={6} align="stretch">
        <Alert status="info" borderRadius="xl">
          <AlertIcon />
          <Box>
            <AlertTitle>No Optimization Data</AlertTitle>
            <AlertDescription>
              This listing hasn't been processed by the optimization system yet. Run the
              optimization process to analyze competitors and get pricing recommendations.
            </AlertDescription>
          </Box>
        </Alert>
      </VStack>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Main Content Grid */}
      <HStack spacing={4} align="stretch" h="100%">
        {/* Left Column - Status & Pricing */}
        <VStack spacing={4} flex="3" align="stretch" h="100%">
          {/* Optimization Status & Pricing */}
          <Card bg={bgColor} border="1px solid" borderColor={borderColor} flex="1">
            <CardHeader pb={3}>
              <Flex justify="space-between" align="center">
                <Heading size="md" color={headingColor}>
                  Optimization Status
                </Heading>
                {getStatusBadge(optimization.status)}
              </Flex>
            </CardHeader>
            <CardBody pt={0}>
              <VStack spacing={3} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="sm" color={labelColor}>
                    Last Optimized:
                  </Text>
                  <Text fontSize="sm" fontWeight="medium" color={textColor}>
                    {formatDateTime(optimization.lastOptimizedAt) || 'N/A'}
                  </Text>
                </HStack>

                {optimization.optimizationNotes && (
                  <Box>
                    <Text fontSize="sm" color={labelColor} mb={1}>
                      Notes:
                    </Text>
                    <Text fontSize="sm" color={textColor} fontStyle="italic">
                      {optimization.optimizationNotes}
                    </Text>
                  </Box>
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* Pricing Analysis */}
          <Card bg={bgColor} border="1px solid" borderColor={borderColor} flex="1">
            <CardHeader pb={3}>
              <Heading size="md" color={headingColor}>
                Pricing Analysis
              </Heading>
            </CardHeader>
            <CardBody pt={0}>
              <VStack spacing={4} align="stretch">
                {/* Current vs Suggested Price */}
                <HStack spacing={6} justify="space-between">
                  <Stat textAlign="center">
                    <StatLabel fontSize="sm" color={labelColor}>
                      Current Price
                    </StatLabel>
                    <StatNumber fontSize="xl" color={headingColor}>
                      {formatPrice(listing?.details?.price)}
                    </StatNumber>
                  </Stat>

                  <Box alignSelf="center">
                    <Text fontSize="sm" color={labelColor}>
                      vs
                    </Text>
                  </Box>

                  <Stat textAlign="center">
                    <StatLabel fontSize="sm" color={labelColor}>
                      Suggested Price
                    </StatLabel>
                    <StatNumber fontSize="xl" color={successColor}>
                      {formatPrice(optimization.suggestedPrice)}
                    </StatNumber>
                  </Stat>
                </HStack>

                {/* Competitor Information */}
                {optimization.competitorInfo && (
                  <>
                    <Divider />
                    <HStack spacing={6} justify="space-between">
                      <Stat textAlign="center">
                        <StatLabel fontSize="sm" color={labelColor}>
                          Competitor Price
                        </StatLabel>
                        <StatNumber fontSize="lg" color={headingColor}>
                          {formatPrice(optimization.competitorPrice)}
                        </StatNumber>
                      </Stat>

                      <Stat textAlign="center">
                        <StatLabel fontSize="sm" color={labelColor}>
                          Price Difference
                        </StatLabel>
                        <StatNumber
                          fontSize="lg"
                          color={
                            optimization.competitorPrice > (listing?.details?.price || 0)
                              ? successColor
                              : errorColor
                          }
                        >
                          {formatPrice(
                            (listing?.details?.price || 0) -
                              (optimization.competitorPrice || 0),
                          )}
                        </StatNumber>
                        <StatHelpText fontSize="xs" color={labelColor}>
                          {optimization.competitorPrice > 0 && listing?.details?.price
                            ? `${(((listing.details.price - optimization.competitorPrice) / optimization.competitorPrice) * 100).toFixed(1)}% difference`
                            : 'N/A'}
                        </StatHelpText>
                      </Stat>
                    </HStack>
                  </>
                )}
              </VStack>
            </CardBody>
          </Card>
        </VStack>

        {/* Right Column - Settings */}
        <VStack spacing={4} flex="2" align="stretch" h="100%">
          {/* Optimization Settings */}
          <Card bg={bgColor} border="1px solid" borderColor={borderColor} flex="1">
            <CardHeader pb={3}>
              <Heading size="md" color={headingColor}>
                Settings
              </Heading>
            </CardHeader>
            <CardBody pt={0}>
              <VStack spacing={3} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="sm" color={labelColor}>
                    Net Margin:
                  </Text>
                  <Text fontSize="sm" fontWeight="medium" color={textColor}>
                    {optimization.minimumNetMarkup
                      ? `${optimization.minimumNetMarkup}%`
                      : 'N/A'}
                  </Text>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" color={labelColor}>
                    Commission:
                  </Text>
                  <Text fontSize="sm" fontWeight="medium" color={textColor}>
                    {optimization.marketplaceCommissionMarkup
                      ? `${optimization.marketplaceCommissionMarkup}%`
                      : 'N/A'}
                  </Text>
                </HStack>

                <HStack justify="space-between">
                  <Text fontSize="sm" color={labelColor}>
                    Price Undercut:
                  </Text>
                  <Text fontSize="sm" fontWeight="medium" color={textColor}>
                    {optimization.priceUndercutPercent
                      ? `${optimization.priceUndercutPercent}%`
                      : 'N/A'}
                  </Text>
                </HStack>

                {optimization.addedOverallMarkup && (
                  <HStack justify="space-between">
                    <Text fontSize="sm" color={labelColor}>
                      Added Markup:
                    </Text>
                    <Text fontSize="sm" fontWeight="semibold" color={successColor}>
                      {optimization.addedOverallMarkup.toFixed(2)}%
                    </Text>
                  </HStack>
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* Competitor Details - Moved to right column */}
          {optimization.competitorInfo && (
            <Card bg={bgColor} border="1px solid" borderColor={borderColor} flex="1">
              <CardHeader pb={3}>
                <Heading size="md" color={headingColor}>
                  Selected Competitor
                </Heading>
              </CardHeader>
              <CardBody pt={0}>
                <VStack spacing={3} align="stretch">
                  <HStack spacing={3} align="start">
                    {optimization.competitorInfo.image?.imageUrl && (
                      <Image
                        src={optimization.competitorInfo.image.imageUrl}
                        alt={optimization.competitorInfo.title}
                        boxSize="50px"
                        objectFit="contain"
                        borderRadius="md"
                        fallbackSrc="/placeholder-image.jpg"
                      />
                    )}
                    <VStack align="start" spacing={1} flex={1}>
                      <Text
                        fontSize="xs"
                        fontWeight="medium"
                        color={textColor}
                        noOfLines={2}
                      >
                        {optimization.competitorInfo.title}
                      </Text>
                      <Text fontSize="xs" color={labelColor}>
                        {optimization.competitorInfo.seller?.username || 'Unknown'}
                      </Text>
                      <HStack spacing={1} wrap="wrap">
                        {optimization.competitorInfo.seller?.feedbackPercentage && (
                          <Badge colorScheme="green" variant="outline" fontSize="2xs">
                            {optimization.competitorInfo.seller.feedbackPercentage}%
                          </Badge>
                        )}
                        {optimization.competitorInfo.shippingOptions?.[0]?.shippingCost
                          ?.value === '0.00' && (
                          <Badge colorScheme="blue" variant="outline" fontSize="2xs">
                            Free
                          </Badge>
                        )}
                      </HStack>
                    </VStack>
                    <VStack align="end" spacing={1}>
                      <Text fontSize="sm" fontWeight="bold" color={headingColor}>
                        {formatPrice(optimization.competitorInfo.price?.value)}
                      </Text>
                      {optimization.competitorInfo.legacyItemId && (
                        <Tooltip label="Open on eBay">
                          <IconButton
                            icon={<FaExternalLinkAlt />}
                            size="xs"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() =>
                              window.open(
                                `https://www.ebay.com/itm/${optimization.competitorInfo.legacyItemId}`,
                                '_blank',
                              )
                            }
                          />
                        </Tooltip>
                      )}
                    </VStack>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          )}
        </VStack>
      </HStack>

      {/* Search Results */}
      {optimization.searchResults && optimization.searchResults.length > 0 && (
        <Card bg={bgColor} border="1px solid" borderColor={borderColor}>
          <CardHeader pb={3}>
            <Heading size="md" color={headingColor}>
              All Competitors ({optimization.searchResults.length})
            </Heading>
          </CardHeader>
          <CardBody pt={0}>
            <Box maxH="300px" overflowY="auto">
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Image</Th>
                    <Th>Title</Th>
                    <Th>Seller</Th>
                    <Th>Price</Th>
                    <Th>Rating</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {optimization.searchResults.slice(0, 8).map((competitor, index) => (
                    <Tr key={competitor.itemId || index}>
                      <Td>
                        <Image
                          src={competitor.image?.imageUrl || '/placeholder-image.jpg'}
                          alt={competitor.title}
                          boxSize="30px"
                          objectFit="contain"
                          borderRadius="md"
                          fallbackSrc="/placeholder-image.jpg"
                        />
                      </Td>
                      <Td>
                        <Text fontSize="xs" noOfLines={2} maxW="150px">
                          {competitor.title}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="xs">{competitor.seller?.username || 'N/A'}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="xs" fontWeight="medium">
                          {formatPrice(competitor.price?.value)}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="xs">
                          {competitor.seller?.feedbackPercentage || 'N/A'}%
                        </Text>
                      </Td>
                      <Td>
                        {competitor.legacyItemId && (
                          <Tooltip label="Open on eBay">
                            <IconButton
                              icon={<FaExternalLinkAlt />}
                              size="xs"
                              variant="ghost"
                              colorScheme="blue"
                              onClick={() =>
                                window.open(
                                  `https://www.ebay.com/itm/${competitor.legacyItemId}`,
                                  '_blank',
                                )
                              }
                            />
                          </Tooltip>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
              {optimization.searchResults.length > 8 && (
                <Text fontSize="xs" color={labelColor} textAlign="center" py={2}>
                  Showing first 8 of {optimization.searchResults.length} competitors
                </Text>
              )}
            </Box>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
};

export default OptimizerTab;
